import React, { FC, useState, useMemo } from "react";
import { useSelector } from "react-redux";

import { copyLink, downloadInvoice } from "./handlers";
import { InvoiceSidebarWrapper } from "./InvoiceSidebarWrapper";
import { InvoiceStatusCheck } from "./InvoiceStatusCheck";
import { VerifyForm } from "../../forms/settings";
import { DeclineInvoiceModal } from "../../modals/declineInvoiceModal";
import { Button, Icon, Message } from "../../../components/common";
import {
  CURRENCY_MAP,
  DEFAULT_CRYPTO_CURRENCY,
  DEFAULT_FIAT_CURRENCY,
} from "../../../constants";
import { useTriggerToastOnRTKError } from "../../../hooks/useTriggerToastOnRTKError";
import { useValidateInvoiceAmountThreshold } from "../../../hooks/useValidateInvoiceAmountThreshold";
import { useValidateInvoiceAmountWithFirstKYCLevel } from "../../../hooks/useValidateInvoiceAmountWithFirstKYCLevel";
import {
  Currency,
  CurrencyType,
  InvoiceStatus,
  type Invoice,
} from "../../../types/InvoiceTypes";
import {
  useApproveInvoiceMutation,
  useIssueInvoiceMutation,
} from "../../../store/services/invoices";
import { selectCurrentUser } from "../../../store/reducers/user";
import { formatAmount } from "../../../utils/numbers/money";
import {
  checkIssuerDetails,
  checkPayerDetails,
  checkJobDetails,
  checkWallet,
} from "../../../utils/prepareData/invoiceFilledStatus";
import { useVerifyForm } from "../../../hooks/useVerifyForm";

type Props = {
  className?: string;
  data: Invoice;
};

const getTitle = (
  userIsCreator?: boolean,
  userIsPayer?: boolean,
  approvedByCreator?: boolean,
  isDeclined?: boolean,
  payerName?: string
) => ({
  [InvoiceStatus.DRAFT]: isDeclined
    ? userIsCreator
      ? "The invoice has been declined"
      : "You declined the invoice"
    : approvedByCreator
      ? userIsPayer
        ? "The invoice is sent for review"
        : `${payerName} wants to pay you`
      : "Fill out the payment details to proceed",
  [InvoiceStatus.PENDING]: userIsPayer
    ? "The invoice is ready to pay"
    : "Waiting for the payment",
  [InvoiceStatus.PENDING_REVIEW]: "Payment is under review",
  [InvoiceStatus.PROCESSING]: "Payment is being processed",
  [InvoiceStatus.PAID]: userIsPayer ? "Paid!" : "You're paid!",
  [InvoiceStatus.CANCELLED]: undefined,
});

const getText = (
  userIsCreator?: boolean,
  userIsPayer?: boolean,
  approvedByCreator?: boolean,
  isDeclined?: boolean
) => ({
  [InvoiceStatus.DRAFT]: isDeclined
    ? userIsCreator
      ? userIsPayer
        ? "Please discuss the invoice with the recipient and adjust details"
        : "Please discuss the invoice with the payer and adjust details"
      : userIsPayer
        ? "Waiting for the recipient to adjust the invoice details"
        : "Waiting for the payer to adjust the invoice details"
    : approvedByCreator
      ? userIsPayer
        ? "You will be able to pay once the recipient approves it"
        : "Please review the invoice, set an invoice number, select a crypto wallet and approve the invoice to get the payment"
      : "You can share the draft invoice with the recipient to get feedback if everything looks good to them",
  [InvoiceStatus.PENDING]: userIsPayer
    ? "Please use the bank details revealed in the green box to pay the invoice"
    : "We'll let you know once we get the payment from the payer and crypto is sent to your wallet",
  [InvoiceStatus.PENDING_REVIEW]:
    "We received payment and it is being reviewed",
  [InvoiceStatus.PROCESSING]: "We received payment and it is being processed",
  [InvoiceStatus.PAID]: userIsPayer
    ? "The recipient has successfully received crypto on the wallet"
    : "We got the payment and sent crypto to your wallet",
  [InvoiceStatus.CANCELLED]: undefined,
});

export const InvoiceSidebarDefault: FC<Props> = ({ data, className }) => {
  const [showDeclinedModal, setShowDeclinedModal] = useState(false);
  const currentUser = useSelector(selectCurrentUser);
  const { kyc_complete_and_approved, requires_first_level_kyc } = currentUser;

  const { valid: isUnderFirstLevelThreshold } =
    useValidateInvoiceAmountWithFirstKYCLevel(data);

  const {
    status,
    user_is_creator,
    user_is_issuer,
    user_is_payer,
    approved_by_creator,
    declined_reason,
    payer_id,
    amount,
    currency,
    outgoing_currency,
    min_amount_threshold,
  } = data;

  const minAmountThresholdValue = min_amount_threshold
    ? min_amount_threshold
    : 0;

  const { msg: amountThresholdMsg } = useValidateInvoiceAmountThreshold(
    data,
    "sidebar"
  );

  const [approveInvoice, { isError: isErrorApprove, error: errorApprove }] =
    useApproveInvoiceMutation();

  const [issueInvoice, { isError: isErrorIssue, error: errorIssue }] =
    useIssueInvoiceMutation();

  useTriggerToastOnRTKError(isErrorApprove, errorApprove);
  useTriggerToastOnRTKError(isErrorIssue, errorIssue);

  const { valid: issuerDetailsStatus } = useMemo(
    () => checkIssuerDetails(data),
    [data]
  );

  const { valid: payerDetailsStatus } = useMemo(
    () => checkPayerDetails(data),
    [data]
  );

  const jobDetailsStatus = useMemo(() => checkJobDetails(data), [data]);
  const walletStatus = useMemo(() => checkWallet(data), [data]);
  const currencyDetails = CURRENCY_MAP[currency || DEFAULT_FIAT_CURRENCY];

  const outgoingCurrencyDetails =
    CURRENCY_MAP[outgoing_currency || DEFAULT_CRYPTO_CURRENCY];

  const approveButtonLabel = !payer_id
    ? `Issue invoice for ${formatAmount(amount)} ${currencyDetails.symbol}`
    : user_is_creator
      ? "Send for review"
      : "Accept invoice";

  const canApprove =
    ((!requires_first_level_kyc &&
      user_is_issuer &&
      (kyc_complete_and_approved || isUnderFirstLevelThreshold) &&
      ((currency === Currency.USDC && outgoing_currency === Currency.EUR) ||
        walletStatus)) ||
      user_is_payer) &&
    issuerDetailsStatus &&
    payerDetailsStatus &&
    jobDetailsStatus &&
    amount >= minAmountThresholdValue;

  const isActionable =
    status === InvoiceStatus.DRAFT &&
    ((user_is_creator && (!approved_by_creator || declined_reason)) ||
      (!user_is_creator && approved_by_creator && !declined_reason));

  const sendInvoiceHandler = async () => {
    if (data) {
      if (!payer_id || (!declined_reason && approved_by_creator)) {
        await issueInvoice(data.id);
      } else {
        await approveInvoice(data.id);
      }
    }
  };

  const declineInvoiceHandler = () => setShowDeclinedModal(true);
  const showVerifyForm = useVerifyForm(user_is_issuer);

  return (
    <>
      <InvoiceSidebarWrapper
        className={className}
        title={
          getTitle(
            user_is_creator,
            user_is_payer,
            approved_by_creator,
            !!declined_reason,
            data.payer_data?.name
          )[status]
        }
        text={
          getText(
            user_is_creator,
            user_is_payer,
            approved_by_creator,
            !!declined_reason
          )[status]
        }
      >
        {declined_reason && (
          <Message heading="Reason" text={declined_reason} className="mb-12" />
        )}

        {isActionable ? (
          <>
            <InvoiceStatusCheck />

            <div className="flex flex-col gap-4 mt-8 mb-8">
              <Button
                onClick={sendInvoiceHandler}
                variant={Button.variant.TEMPTING}
                className="h-auto"
                contentClassName="flex flex-row justify-between items-center w-full text-left gap-4"
                disabled={!canApprove}
              >
                {approveButtonLabel}
                <Icon icon="Thumb-up" size={24} disableFill />
              </Button>

              {user_is_creator ? (
                <div className="font-manrope text-basefont-semibold text-gray-300">
                  You won’t be able to edit after it's issued
                </div>
              ) : (
                <>
                  <Button
                    onClick={(e) => declineInvoiceHandler()}
                    contentClassName="flex flex-row justify-between items-center w-full"
                  >
                    Decline invoice &nbsp;
                    <Icon
                      icon="Close"
                      size={24}
                      className="text-gray-500"
                      disableFill
                    />
                  </Button>

                  <div className="font-manrope text-basefont-semibold text-gray-300">
                    Please let the partner know why you declined the invoice so
                    they can adjust it
                  </div>
                </>
              )}
            </div>
          </>
        ) : (
          <div className="flex flex-col gap-y-3 mb-8">
            <Button
              onClick={(e) => copyLink(e, data?.public_id || "")}
              contentClassName="flex flex-row justify-between items-center w-full"
            >
              Copy link &nbsp;
              <Icon
                icon="Link"
                size={24}
                className="text-gray-500"
                disableFill
              />
            </Button>

            <Button
              onClick={(e) => downloadInvoice(e, data, currentUser)}
              contentClassName="flex flex-row justify-between items-center w-full"
            >
              Download invoice &nbsp;
              <Icon
                icon="PDF"
                size={24}
                className="text-gray-500"
                disableFill
              />
            </Button>
          </div>
        )}

        {showVerifyForm && (
          <VerifyForm
            invoice={data}
            text={
              outgoingCurrencyDetails.type === CurrencyType.FIAT
                ? `To issue invoice and get payout in ${outgoingCurrencyDetails.name}`
                : undefined
            }
            className="mb-12"
          />
        )}

        {amountThresholdMsg}
      </InvoiceSidebarWrapper>

      <DeclineInvoiceModal
        id={data.id}
        isOpen={showDeclinedModal}
        onClose={() => setShowDeclinedModal(false)}
      />
    </>
  );
};
